import { Routes, Route, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { Layout } from './components/layout/Layout';
import { Login } from './pages/Login';
import { Onboarding } from './pages/Onboarding';
import { Privacy } from './pages/Privacy';
import { Service } from './pages/Service';
import { useAuthStore } from './store/authStore';
import { usersAPI } from './services/api';
import { Loader2 } from 'lucide-react';

// Lazy load pages for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard').then(module => ({ default: module.Dashboard })));
const Assets = lazy(() => import('./pages/Assets').then(module => ({ default: module.Assets })));
const AssetDetail = lazy(() => import('./pages/AssetDetail').then(module => ({ default: module.AssetDetail })));
const ProtocolGraph = lazy(() => import('./pages/ProtocolGraph').then(module => ({ default: module.ProtocolGraph })));
const ModelPlayground = lazy(() => import('./pages/ModelPlayground').then(module => ({ default: module.ModelPlayground })));
const Alerts = lazy(() => import('./pages/Alerts').then(module => ({ default: module.Alerts })));

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
  </div>
);

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  const { data: profile, isLoading } = useQuery({
    queryKey: ['user-profile'],
    queryFn: () => usersAPI.getProfile(),
    enabled: isAuthenticated,
    retry: false,
  });

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // 온보딩 완료 여부 확인
  const metadata = (profile as any)?.data?.metadata;
  const onboardingCompleted = metadata?.onboarding_completed === true;

  if (!onboardingCompleted) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/service" element={<Service />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route 
          path="dashboard" 
          element={
            <Suspense fallback={<PageLoader />}>
              <Dashboard />
            </Suspense>
          } 
        />
        <Route 
          path="assets" 
          element={
            <Suspense fallback={<PageLoader />}>
              <Assets />
            </Suspense>
          } 
        />
        <Route 
          path="assets/:id" 
          element={
            <Suspense fallback={<PageLoader />}>
              <AssetDetail />
            </Suspense>
          } 
        />
        <Route 
          path="graph" 
          element={
            <Suspense fallback={<PageLoader />}>
              <ProtocolGraph />
            </Suspense>
          } 
        />
        <Route 
          path="playground" 
          element={
            <Suspense fallback={<PageLoader />}>
              <ModelPlayground />
            </Suspense>
          } 
        />
        <Route 
          path="alerts" 
          element={
            <Suspense fallback={<PageLoader />}>
              <Alerts />
            </Suspense>
          } 
        />
      </Route>
    </Routes>
  );
}

export default App;

